import type { Block, BlockType } from '@/lib/workflow';
import styles from './TemplatePreview.module.css';

/**
 * The tile image.
 *
 * Stands in for a screenshot of the live dashboard, and is built from the
 * template's own block list — so it is a truthful miniature of what the
 * workflow renders rather than generic art. When real screenshots exist this
 * component is the seam to swap them in behind.
 *
 * Deliberately not to scale and not data-bound: it reads as a thumbnail, not
 * as a chart with numbers a viewer might try to read.
 */

/** Miniature of a single block, drawn per type. */
function MiniBlock({ type }: { type: BlockType }) {
  if (type === 'kpis') {
    return (
      <div className={styles.kpis}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={styles.kpi}>
            <span className={styles.kpiBar} data-i={i} />
          </div>
        ))}
      </div>
    );
  }

  if (type === 'timeseries') {
    return (
      <div className={styles.chart}>
        <svg viewBox="0 0 100 32" preserveAspectRatio="none" className={styles.svg} aria-hidden="true">
          <path
            d="M0 26 L12 22 L24 24 L36 15 L48 18 L60 9 L72 12 L84 5 L100 8 L100 32 L0 32 Z"
            className={styles.area}
          />
          <path
            d="M0 26 L12 22 L24 24 L36 15 L48 18 L60 9 L72 12 L84 5 L100 8"
            className={styles.line}
          />
        </svg>
      </div>
    );
  }

  if (type === 'donut') {
    return (
      <div className={styles.donutRow}>
        <svg viewBox="0 0 36 36" className={styles.donut} aria-hidden="true">
          {/* Three arcs via dash offsets — no data, just a recognisable ring */}
          <circle cx="18" cy="18" r="14" className={styles.ring} />
          <circle cx="18" cy="18" r="14" className={styles.arc1} />
          <circle cx="18" cy="18" r="14" className={styles.arc2} />
        </svg>
        <span className={styles.legend}>
          {[0, 1, 2].map((i) => (
            <span key={i} className={styles.legendRow}>
              <span className={styles.legendDot} data-i={i} />
              <span className={styles.legendBar} data-i={i} />
            </span>
          ))}
        </span>
      </div>
    );
  }

  if (type === 'split') {
    return (
      <div className={styles.split}>
        <span className={styles.splitLeft} />
        <span className={styles.splitRight} />
      </div>
    );
  }

  if (type === 'table') {
    return (
      <div className={styles.table}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={styles.tableRow} data-i={i} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.note}>
      {[0, 1].map((i) => (
        <span key={i} className={styles.noteLine} data-i={i} />
      ))}
    </div>
  );
}

interface Props {
  blocks: Block[];
  category: string;
}

export function TemplatePreview({ blocks, category }: Props) {
  /* Four blocks is what fits before the miniature stops reading as a layout. */
  const shown = blocks.slice(0, 4);

  return (
    <div className={styles.base} data-category={category} aria-hidden="true">
      <div className={styles.chrome}>
        <span className={styles.chromeDot} />
        <span className={styles.chromeTitle} />
      </div>
      <div className={styles.body}>
        {shown.map((block) => (
          <div key={block.id} className={styles.block} data-type={block.type}>
            <span className={styles.blockLabel} />
            <MiniBlock type={block.type} />
          </div>
        ))}
      </div>
    </div>
  );
}
